import { getInitData, tg } from './telegram'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export interface Me {
  user_id: number
  user: {
    telegram_id: number
    first_name: string
    username?: string
    language_code?: string
    is_premium?: boolean
  }
  locale: string
  message: string
  profile_complete: boolean
  /** True if the user has uploaded at least one photo (any moderation status). */
  has_photo: boolean
  /** True if the user has at least one *approved* photo — i.e. whether they
   * can actually appear as a candidate in another user's feed (the feed
   * query excludes anyone without one). `has_photo && !has_approved_photo`
   * means their photo(s) are still pending/rejected: they're swiping while
   * invisible to everyone else. */
  has_approved_photo: boolean
  /** True if the user has admin privileges (feed filters, etc.). This is
   * the EFFECTIVE value — if the admin is impersonating a lower tier via
   * the bot's /actas command (see `acting_as`), this is false. */
  is_admin: boolean
  /** True if the user has paused (snoozed) their account — see setPause. */
  is_paused: boolean
  /** True if the user currently has an active VIP pass (real purchase, not
   * the admin preview). VIP-gated UI (Crush/Rewind buttons, wallet card)
   * should key off `is_admin || is_vip`. Also the EFFECTIVE value — see
   * `acting_as`. */
  is_vip: boolean
  /** ISO timestamp the active VIP pass expires at, or null if not VIP. */
  vip_expires_at: string | null
  /** True if the VIP/Crush purchase flow is actually reachable for this
   * caller — reflects only the FEATURES_PAYMENTS_ENABLED kill-switch now
   * (see paymentsGate server-side); the admin-only soak test has ended and
   * the purchase pages are public. UI that offers an "Upgrade VIP" CTA
   * should still gate on this rather than assuming true, so it degrades
   * correctly if the kill-switch is ever flipped off again. */
  payments_enabled: boolean
  /** Non-null only for an admin who sent /actas to the bot: the tier
   * ('free' | 'vip' | 'premium' | 'supreme' | 'admin') they are currently
   * impersonating for testing. `is_admin`/`is_vip`/`payments_enabled` above
   * already reflect this override — the Mini App uses this field only to
   * show a banner explaining why the UI looks the way it does. */
  acting_as: 'free' | 'vip' | 'premium' | 'supreme' | 'admin' | null
  /** True if the caller's effective tier qualifies for the Exclusive Icons
   * (Crown) Premium perk — split from `crown_hidden` (rather than one
   * resolved boolean) so the Settings sheet can tell "not eligible" apart
   * from "eligible but opted out" and decide whether to render the toggle
   * at all. Also the EFFECTIVE value — see `acting_as`. */
  crown_eligible: boolean
  /** True if the caller has opted out of showing their own crown badge, via
   * setCrownHidden. Independent of `crown_eligible`. */
  crown_hidden: boolean
  /** The caller's EFFECTIVE tier as a string, or null for free — same
   * "effective" meaning as is_admin/is_vip (reflects any live /actas
   * override). Premium and Supreme are real, purchasable tiers now (see
   * migration 000026_vip_order_tier) with their own perks (100 likes/day,
   * 3 free Crush/day, Crown for Premium — Read Receipts retired along with
   * in-app chat, see docs/bot-migration-decision.md's "Phase 1.5") — this field exists
   * purely so the UI can *display* which tier badge to render; every
   * server-side gate only ever needed an AtLeast check, never the exact
   * tier. An unconstrained admin (no live /actas override) reports
   * "admin" rather than null — acting_as below already exists to explain
   * that state. */
  active_tier: VIPTierName | 'admin' | null
}

export type GenderFilter = 'any' | 'male' | 'female'

export interface FeedFilter {
  age_min: number
  age_max: number
  gender: GenderFilter
  radius_km: number
}

export interface ProfilePayload {
  display_name: string
  birthdate: string // YYYY-MM-DD
  gender: 'male' | 'female'
  seeking: 'male' | 'female' | 'both'
  bio?: string
  city?: string
  interests?: string[]
  education?: string
  height_cm?: number
}

export interface ProfileResponse {
  profile_complete: boolean
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Parsed JSON error body, when the response was valid JSON. Lets
     * callers branch on structured fields (e.g. `error`, `limit`,
     * `resets_at`) instead of substring-matching `message`. */
    public readonly body?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface Candidate {
  user_id: number
  display_name: string
  age: number
  bio?: string
  city: string
  distance_km: number
  photo_urls: string[]
  interests?: string[]
  education?: string
  height_cm?: number
  /** True when this candidate previously sent the viewer a Crush and the
   * viewer hasn't decided on them yet — see injectCrushCandidates in
   * apps/api/feed_handlers.go. */
  crushed_you?: boolean
  /** True when this candidate's Exclusive Icons (Crown) Premium badge is
   * visible — already resolved server-side (kill-switch, tier, their own
   * opt-out). See crownVisible in apps/api/crown.go. */
  crown?: boolean
}

export interface PartnerProfile {
  user_id: number
  display_name: string
  age: number
  bio?: string
  city?: string
  /** Present only when opened from a swipe card (distance is known). */
  distance_km?: number
  interests?: string[]
  education?: string
  height_cm?: number
  photo_urls: string[]
  /** See Candidate.crown. */
  crown?: boolean
  /** Only present when opened from a match — the direct-message handoff
   * mechanism (docs/bot-migration-decision.md's "Phase 1.5 — Chat
   * retirement"): absent means the partner hasn't set a Telegram username
   * yet, so they can't be linked to directly. */
  telegram_username?: string
}

export interface SwipeResponse {
  matched: boolean
  match_id?: number
}

export interface RewindResponse {
  ok: boolean
  /** The undone candidate's full profile, or null if they no longer qualify
   * under the viewer's own filters (banned, blocked, etc. since the
   * original swipe) — the undo itself still succeeded either way. */
  candidate: Candidate | null
}

export async function fetchMe(): Promise<Me> {
  return apiGet<Me>('/api/v1/me')
}

/** Consumable balances (Crush today; Turbo/Praise will join once those
 * features exist — see docs/monetization-roadmap.md). */
export interface WalletBalances {
  crush: number
}

export async function fetchWallet(): Promise<WalletBalances> {
  return apiGet<WalletBalances>('/api/v1/me/wallet')
}

/** One purchasable Crush pack SKU (docs/monetization-roadmap.md "Payment
 * Feature"). Price is always server-authoritative — the client only ever
 * picks a pack_size, never sends an amount. */
export interface CrushPack {
  pack_size: number
  price_idr: number
}

export async function fetchCrushPacks(): Promise<CrushPack[]> {
  const data = await apiGet<{ packs: CrushPack[] }>('/api/v1/payments/crush-packs')
  return data.packs ?? []
}

export interface CrushOrder {
  id: number
  /** The crypto-random merchant_ref string — used to build the
   * qris-image download proxy URL (see qrisDownloadURL), since that route
   * is unauthenticated and can't be keyed by the sequential numeric id. */
  order_id: string
  /** Present even while 'pending'/'expired' on any order created after this
   * field shipped — lets the expired screen regenerate a QR for the same
   * pack in one tap, without re-fetching crush-packs or asking the buyer to
   * pick a size again. Optional, not guaranteed: an order resumed from a
   * stale localStorage entry (see pendingOrder.ts) predating this field
   * won't have it. */
  pack_size?: number
  status: 'pending' | 'completed' | 'expired' | 'failed'
  /** URL to a hosted QRIS image (Saku Rupiah) — NOT a raw EMV payload
   * string, despite what the name might suggest historically. Rendered via
   * <img src=...>, not a client-side QR-code-from-payload library. Do NOT
   * pass this to downloadFile() — it lacks the headers Telegram requires
   * for a real download; use qrisDownloadURL(order_id) instead. */
  qris_image_url?: string
  /** Saku Rupiah's hosted invoice page — a fallback if qris_image_url ever
   * fails to load. */
  checkout_url?: string
  total_payment_idr?: number
  expires_at?: string
  /** Present once status is 'completed'. */
  crush_balance?: number
}

export async function createCrushOrder(packSize: number): Promise<CrushOrder> {
  const data = await apiPost<{
    id: number
    order_id: string
    pack_size: number
    qris_image_url: string
    checkout_url: string
    total_payment_idr: number
    expires_at: string
  }>('/api/v1/payments/crush-orders', { pack_size: packSize })
  return { ...data, status: 'pending' }
}

/** Polls a Crush order's status — see BuyCrushPage. Doubles as a
 * reconciliation path independent of the Saku Rupiah webhook (see docs/api.md). */
export async function fetchCrushOrder(id: number): Promise<CrushOrder> {
  return apiGet<CrushOrder>(`/api/v1/payments/crush-orders/${id}`)
}

/** The three purchasable subscription tiers — VIP/Premium/Supreme, in
 * ladder order (see docs/monetization-roadmap.md "Kejar Premium Feature").
 * Supreme has no differentiating features shipped yet, so the backend
 * withholds it from GET /payments/vip-tiers unconditionally — it simply
 * never appears in a VIPSku list, so any UI that filters by the SKUs it
 * actually received naturally never shows a Supreme option either. */
export type VIPTierName = 'vip' | 'premium' | 'supreme'

/** One purchasable (tier × duration) SKU (docs/monetization-roadmap.md
 * "Pricing"). Price is always server-authoritative. Renamed from the old
 * VIP-only `VIPTier` — that name now means "duration SKU" here but "product
 * line" in `tier`, so keeping both under one name would be confusing. */
export interface VIPSku {
  tier: VIPTierName
  duration_days: number
  price_idr: number
}

export async function fetchVIPSkus(): Promise<VIPSku[]> {
  const data = await apiGet<{ tiers: VIPSku[] }>('/api/v1/payments/vip-tiers')
  return data.tiers ?? []
}

export interface VIPOrder {
  id: number
  /** The crypto-random merchant_ref string — used to build the
   * qris-image download proxy URL (see qrisDownloadURL), since that route
   * is unauthenticated and can't be keyed by the sequential numeric id. */
  order_id: string
  tier: VIPTierName
  /** Present even while 'pending'/'expired' on any order created after this
   * field shipped — lets the expired screen regenerate a QR for the same
   * SKU in one tap, without asking the buyer to pick a duration again.
   * Optional, not guaranteed: an order resumed from a stale localStorage
   * entry (see pendingOrder.ts) predating this field won't have it. */
  duration_days?: number
  status: 'pending' | 'completed' | 'expired' | 'failed'
  /** URL to a hosted QRIS image (Saku Rupiah) — NOT a raw EMV payload
   * string. Rendered via <img src=...>. Do NOT pass this to downloadFile()
   * — it lacks the headers Telegram requires for a real download; use
   * qrisDownloadURL(order_id) instead. */
  qris_image_url?: string
  /** Saku Rupiah's hosted invoice page — a fallback if qris_image_url ever
   * fails to load. */
  checkout_url?: string
  total_payment_idr?: number
  expires_at?: string
  /** Present once status is 'completed'. */
  vip_expires_at?: string
}

export async function createVIPOrder(tier: VIPTierName, durationDays: number): Promise<VIPOrder> {
  const data = await apiPost<{
    id: number
    order_id: string
    tier: VIPTierName
    duration_days: number
    qris_image_url: string
    checkout_url: string
    total_payment_idr: number
    expires_at: string
  }>('/api/v1/payments/vip-orders', { tier, duration_days: durationDays })
  return { ...data, status: 'pending' }
}

/** Polls a VIP order's status — see BuyVIPPage. Same reconciliation-loop
 * design as fetchCrushOrder. */
export async function fetchVIPOrder(id: number): Promise<VIPOrder> {
  return apiGet<VIPOrder>(`/api/v1/payments/vip-orders/${id}`)
}

/**
 * Builds the URL for the backend's QRIS image download proxy (unauthenticated
 * — see apps/api/payment_handlers.go's handleQRISImageDownload). Pass this,
 * not the raw qris_image_url, to downloadFile(): Saku Rupiah's image host
 * doesn't send the Content-Disposition/Access-Control-Allow-Origin headers
 * Telegram's docs require for a real (not just a viewed) download, so our
 * backend fetches the image and re-serves it with those headers (and, for
 * mobile clients, proper Range/Accept-Ranges/HEAD support via
 * http.ServeContent — see that handler's doc comment).
 *
 * The .png suffix is deliberate, not cosmetic: some native downloaders
 * derive the filename/MIME from the URL path itself, and the backend
 * strips it back off before the order_id lookup, so this is safe.
 */
export function qrisDownloadURL(orderId: string): string {
  return `${BASE_URL}/api/v1/payments/qris-image/${orderId}.png`
}

export interface FeedResponse {
  candidates: Candidate[]
  /** Present only when the request carried `reveal` — see fetchFeed's
   * `reveal` option. True if the requested user still qualified and was
   * force-injected at the front of `candidates`; false if they no longer do
   * (unapproved photo since, mutually blocked, etc.) — the caller's "See
   * Who Likes You" tap still consumed their authorization either way, so
   * the UI should say so honestly rather than silently showing a normal
   * deck. */
  revealed?: boolean
}

export async function fetchFeed(opts?: {
  radius_km?: number
  limit?: number
  /** "See Who Likes You" reveal (docs/monetization-roadmap.md "Kejar
   * Premium Feature" #4): the user_id of someone who liked the caller,
   * obtained from fetchIncomingLikes (Premium+ only — see its `can_reveal`).
   * Server re-checks both the tier gate and that this id actually liked the
   * caller, so passing an arbitrary id here just 403s rather than leaking
   * anything. */
  reveal?: number
}): Promise<FeedResponse> {
  const params = new URLSearchParams()
  if (opts?.radius_km) params.set('radius_km', String(opts.radius_km))
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.reveal) params.set('reveal', String(opts.reveal))
  const qs = params.toString()
  const data = await apiGet<FeedResponse>(`/api/v1/feed${qs ? '?' + qs : ''}`)
  return { candidates: data.candidates ?? [], revealed: data.revealed }
}

export async function swipe(
  targetUserId: number,
  action: 'like' | 'skip' | 'crush',
): Promise<SwipeResponse> {
  return apiPost<SwipeResponse>('/api/v1/swipes', { target_user_id: targetUserId, action })
}

export async function rewind(): Promise<RewindResponse> {
  return apiPost<RewindResponse>('/api/v1/swipes/rewind', undefined)
}

export async function updateLocation(lat: number, lon: number): Promise<void> {
  await apiPatch<unknown>('/api/v1/profile/location', { lat, lon })
}

export async function getFeedFilters(): Promise<FeedFilter> {
  return apiGet<FeedFilter>('/api/v1/feed/filters')
}

export async function updateFeedFilters(f: FeedFilter): Promise<FeedFilter> {
  return apiPatch<FeedFilter>('/api/v1/feed/filters', f)
}

export interface MatchPartner {
  id: number
  display_name: string
  photo_url?: string
  /** See Candidate.crown. */
  crown?: boolean
  /** The direct-message handoff mechanism
   * (docs/bot-migration-decision.md's "Phase 1.5 — Chat retirement") —
   * absent means the partner hasn't set a Telegram username yet. */
  username?: string
}

export interface MatchItem {
  id: number
  partner: MatchPartner
  created_at: string
}

export async function fetchMatches(): Promise<MatchItem[]> {
  const data = await apiGet<{ matches: MatchItem[] }>('/api/v1/matches')
  return data.matches ?? []
}

export interface LikesCount {
  /** How many people currently like the caller (undecided incoming
   * like/superlike) — see fetchIncomingLikes. */
  likes_you: number
}

export async function fetchLikesYouCount(): Promise<LikesCount> {
  const data = await apiGet<Partial<LikesCount>>('/api/v1/unread-count')
  return { likes_you: data.likes_you ?? 0 }
}

/** One row of the "Yang suka kamu" (incoming likes) tab —
 * docs/monetization-roadmap.md "Kejar Premium Feature" #4.
 * `user_id`/`name` are intentionally absent from this type entirely: the
 * server never sends a name here at all (gated or not — identity only ever
 * opens via the swipe deck), and `user_id` is present in the raw response
 * only when `can_reveal` is true. Access it only through
 * `IncomingLikesResponse.items[i].user_id`, which is typed to require that
 * check. */
export interface IncomingLikeView {
  user_id?: number
  /** Always a server-side-blurred image (see internal/imgproxy.Signer.
   * SignBlurred) — never the original photo, regardless of tier. Absent
   * only if the backend has no imgproxy configured at all. */
  photo_url?: string
  created_at: string
}

export interface IncomingLikesResponse {
  count: number
  /** True only for a real Premium+ caller with the kill-switch on — see
   * apps/api/likes_handlers.go's handleIncomingLikes. Gates whether tapping
   * a tile opens `/swipe?reveal=<id>` or the Premium paywall. */
  can_reveal: boolean
  items: IncomingLikeView[]
}

export async function fetchIncomingLikes(): Promise<IncomingLikesResponse> {
  return apiGet<IncomingLikesResponse>('/api/v1/likes/incoming')
}

/** One row of the free "Like-ku" (outgoing likes) tab. Unlike
 * IncomingLikeView, this is never gated — the caller has already seen and
 * acted on these profiles in their own deck. */
export interface OutgoingLikeView {
  user_id: number
  display_name: string
  photo_url?: string
  created_at: string
}

export async function fetchOutgoingLikes(): Promise<OutgoingLikeView[]> {
  const data = await apiGet<{ items: OutgoingLikeView[] }>('/api/v1/likes/outgoing')
  return data.items ?? []
}

export async function fetchPartnerProfile(matchId: number): Promise<PartnerProfile> {
  return apiGet<PartnerProfile>(`/api/v1/matches/${matchId}/partner`)
}

export type ReportReason = 'spam' | 'fake' | 'inappropriate' | 'harassment'

/** Report a user. */
export async function reportUser(
  reportedUserId: number,
  reason: ReportReason,
): Promise<{ report_id: number }> {
  return apiPost<{ report_id: number }>('/api/v1/reports', {
    reported_user_id: reportedUserId,
    reason,
  })
}

/** Block a user. Also deactivates any existing match with them. */
export async function blockUser(blockedUserId: number): Promise<void> {
  await apiPost<unknown>('/api/v1/blocks', { blocked_user_id: blockedUserId })
}

/** Remove a previously placed block. */
export async function unblockUser(userId: number): Promise<void> {
  await apiFetch('DELETE', `/api/v1/blocks/${userId}`)
}

/** Unmatch (deactivate) a match. */
export async function unmatch(matchId: number): Promise<void> {
  await apiFetch('DELETE', `/api/v1/matches/${matchId}`)
}

export interface Photo {
  id: number
  position: number
  status: 'pending' | 'approved' | 'rejected'
  url?: string
  is_main: boolean
}

export async function listPhotos(): Promise<Photo[]> {
  const data = await apiGet<{ photos: Photo[] }>('/api/v1/photos')
  return data.photos ?? []
}

export async function deletePhoto(id: number): Promise<void> {
  await apiFetch('DELETE', `/api/v1/photos/${id}`)
}

/**
 * Promotes photo `id` to the user's main photo. As of the 3-slot photo grid
 * (PhotoUploadPage), this also swaps the photo into position 1 server-side
 * (see PhotoRepo.PromoteToMain) — position 1 renders as the large slot, so
 * "main photo" and "large slot" must always be the same photo.
 */
export async function setMainPhoto(id: number): Promise<void> {
  await apiFetch('PATCH', `/api/v1/photos/${id}/main`)
}

export async function getProfile(): Promise<ProfilePayload | null> {
  try {
    return await apiGet<ProfilePayload>('/api/v1/profile/me')
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null
    throw e
  }
}

export async function createProfile(payload: ProfilePayload): Promise<ProfileResponse> {
  return apiPost<ProfileResponse>('/api/v1/profile', payload)
}

export async function updateProfile(payload: ProfilePayload): Promise<ProfileResponse> {
  return apiPatch<ProfileResponse>('/api/v1/profile/me', payload)
}

export type OnboardingStep = 'welcome' | 'identity' | 'preference' | 'photo'

/**
 * Reports which onboarding step the user is currently on, for the admin
 * /stats abandonment breakdown. Best-effort — callers should swallow errors
 * so a flaky report never blocks the onboarding UX.
 */
export async function reportOnboardingStep(step: OnboardingStep): Promise<void> {
  await apiPatch<unknown>('/api/v1/onboarding/step', { step })
}

/**
 * The 4 real gated paywall triggers in the Mini App — see
 * docs/paywall-funnel.md for exactly where each fires and why. Not every
 * `/buy-vip`/`/buy-crush` navigation counts: a deliberate menu tap (the
 * ProfilePage tier card, a cross-sell strip, a bot deep link) is not a
 * paywall impression and must not call reportPaywallShown.
 */
export type PaywallReason = 'crush_balance' | 'likes' | 'rewind' | 'likes_you'

/**
 * Reports a paywall impression for the admin funnel measurement (paywall
 * shown -> order created -> paid — see docs/paywall-funnel.md). Fire this
 * right before navigating to the paywall, not after — best-effort, void
 * return, swallows its own errors, so a flaky report never blocks or delays
 * the redirect. Unlike reportBootError this doesn't need `keepalive`:
 * react-router's navigate() is an in-page SPA transition, not a real page
 * unload, so the request completes normally regardless.
 */
export function reportPaywallShown(reason: PaywallReason): void {
  apiPost<unknown>('/api/v1/telemetry/paywall-shown', { reason }).catch(() => {})
}

/**
 * Persists a manual language choice made in SettingsSheet. Best-effort by
 * design — callers should swallow errors so a flaky save never blocks the
 * UI; the local i18next switch already happened independently of this call.
 */
export async function updateLanguage(languageCode: 'en' | 'id'): Promise<void> {
  await apiPatch<unknown>('/api/v1/me/language', { language_code: languageCode })
}

/**
 * Toggles the "pause" (snooze) state set in SettingsSheet — hides the user
 * from other users' swipe feeds and empties their own feed, while leaving
 * existing matches and chats untouched. Free for all users.
 */
export async function setPause(paused: boolean): Promise<{ is_paused: boolean }> {
  return apiPatch<{ is_paused: boolean }>('/api/v1/me/pause', { paused })
}

/**
 * Toggles the Exclusive Icons (Crown) opt-out set in SettingsSheet — only
 * rendered there when `me.crown_eligible` is true. Has no effect on
 * eligibility itself, only on whether an eligible crown is shown.
 */
export async function setCrownHidden(hidden: boolean): Promise<{ crown_hidden: boolean }> {
  return apiPatch<{ crown_hidden: boolean }>('/api/v1/me/crown', { hidden })
}

/**
 * Fixed kinds recognized by the server's client-error telemetry (see
 * apps/api/telemetry_handlers.go). Anything else is reported as "other" so
 * the resulting Prometheus label stays bounded.
 */
export type ClientErrorKind =
  | 'no_initData'
  | 'network_error'
  | 'http_401'
  | 'http_4xx'
  | 'http_5xx'
  | 'other'

/**
 * Classifies an error caught from the boot fetchMe() call into a stable
 * telemetry kind. Exported for testing.
 */
export function classifyBootError(e: unknown): ClientErrorKind {
  if (e instanceof ApiError) {
    if (e.status === 401) return 'http_401'
    if (e.status >= 500) return 'http_5xx'
    if (e.status >= 400) return 'http_4xx'
    return 'other'
  }
  if (e instanceof Error && e.message === 'Not running inside Telegram (no initData)') {
    return 'no_initData'
  }
  // The remaining case is a bare fetch() rejection (TypeError: Failed to
  // fetch) — VPN/DNS/CORS/offline. There's no narrower signal to classify by.
  return 'network_error'
}

/**
 * Best-effort beacon reporting that the Mini App failed to boot — the class
 * of failure (network unreachable, no Telegram initData, an HTTP error from
 * GET /me) that today hides inside the admin /stats "never tapped open"
 * bucket, since none of these requests ever reach the authenticated API
 * group. Posts to an unauthenticated endpoint and never throws: a flaky
 * beacon must never worsen the UX for a user already looking at the error
 * screen.
 */
export function reportBootError(e: unknown): void {
  const kind = classifyBootError(e)
  const message = e instanceof Error ? e.message : String(e)
  const app = tg()
  fetch(BASE_URL + '/api/v1/telemetry/client-error', {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind,
      message,
      platform: app?.platform ?? '',
      tg_version: app?.version ?? '',
      has_init_data: Boolean(getInitData()),
    }),
  }).catch(() => {})
}

async function apiGet<T>(path: string): Promise<T> {
  return apiFetch<T>('GET', path)
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>('POST', path, body)
}

async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>('PATCH', path, body)
}

async function apiFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  const initData = getInitData()
  if (!initData) {
    throw new Error('Not running inside Telegram (no initData)')
  }
  const res = await fetch(BASE_URL + path, {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `tma ${initData}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    let body: Record<string, unknown> | undefined
    try {
      body = text ? (JSON.parse(text) as Record<string, unknown>) : undefined
    } catch {
      body = undefined
    }
    throw new ApiError(res.status, `${res.status}: ${text || res.statusText}`, body)
  }
  return (await res.json()) as T
}
